/**
 * Field-scoped CallLog edit patch (#2061).
 *
 * The Time-tab "Edit visit" flow (ManualCallEntry edit mode) PATCHes an
 * existing CallLog. It used to send the WHOLE core form (times / team flags /
 * notes / odometer / drive time) on every save, regardless of what the rep
 * changed. Because the backend PATCH is partial, that meant a rep who opened a
 * visit before an admin corrected it server-side would silently overwrite the
 * admin's untouched fields with the stale values loaded into the form.
 *
 * buildCallLogEditPatch diffs the form against the server values that were
 * loaded into it (already normalized to HH:MM by the caller's parseTime) and
 * returns ONLY the changed fields. Omitted fields are preserved server-side.
 * Time fields are expanded to full datetimes via the injected `buildDateTime`
 * (kept out of here so the diff stays pure and unit-testable).
 */

/** Server-loaded values, normalized exactly as the edit form loads them. */
export interface CallLogEditBaseline {
  /** Check-in time, HH:MM ('' if none). */
  stcalltime: string;
  /** Check-out time, HH:MM. */
  edcalltime: string;
  teamproject: boolean;
  teamleader: boolean;
  /** Notes ('' if none). */
  notes: string;
  /** Drive-out start/end, HH:MM. */
  stdrivetime: string;
  eddrivetime: string;
  /** Odometer readings as numeric strings ('' if none). */
  driveodostart: string;
  driveodoend: string;
}

/** Current Edit-Visit form values. */
export interface CallLogEditForm {
  timeIn: string;
  timeOut: string;
  isTeamProject: boolean;
  isTeamLeader: boolean;
  visitNotes: string;
  driveTimeStart: string;
  driveTimeEnd: string;
  driveStartOdo: string;
  driveEndOdo: string;
}

/**
 * Build the PATCH body for a CallLog edit, including ONLY fields the rep
 * actually changed vs the loaded baseline. Returns `{}` when nothing changed
 * (caller may skip the network write).
 *
 * @param buildDateTime expands an HH:MM time to the server's full-datetime wire
 *   format for the visit's date (e.g. `t => `${date}T${t}:00``).
 */
export function buildCallLogEditPatch(
  baseline: CallLogEditBaseline,
  form: CallLogEditForm,
  buildDateTime: (hhmm: string) => string
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};

  if (form.timeIn !== baseline.stcalltime) patch.stcalltime = buildDateTime(form.timeIn);
  if (form.timeOut !== baseline.edcalltime) patch.edcalltime = buildDateTime(form.timeOut);
  if (form.isTeamProject !== baseline.teamproject) patch.teamproject = form.isTeamProject;
  if (form.isTeamLeader !== baseline.teamleader) patch.teamleader = form.isTeamLeader;
  // Preserve the prior value semantics: an empty notes is sent as undefined
  // (dropped on the wire), so this path never clears notes — it only forwards
  // a changed, non-empty note.
  if (form.visitNotes !== baseline.notes) patch.notes = form.visitNotes || undefined;

  // Drive time is a start/end pair: send both only when present AND moved.
  if (
    form.driveTimeStart &&
    form.driveTimeEnd &&
    (form.driveTimeStart !== baseline.stdrivetime || form.driveTimeEnd !== baseline.eddrivetime)
  ) {
    patch.stdrivetime = buildDateTime(form.driveTimeStart);
    patch.eddrivetime = buildDateTime(form.driveTimeEnd);
  }

  // Odometer is a start/end pair: send both only when present AND moved.
  if (
    form.driveStartOdo &&
    form.driveEndOdo &&
    (form.driveStartOdo !== baseline.driveodostart || form.driveEndOdo !== baseline.driveodoend)
  ) {
    patch.driveodostart = parseInt(form.driveStartOdo, 10);
    patch.driveodoend = parseInt(form.driveEndOdo, 10);
  }

  return patch;
}
